import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import t from "@/lang/lang";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { DateInput } from "@/shared/components/domain/qpm/inputs/date-input";
import { FloatNumberInput } from "@/shared/components/domain/qpm/inputs/number-input";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import { useQpmOfferSetContractTermsMutation } from "@/services/backend/qpm/offers/api/offer-api";
import {
  Offer,
  SetOfferContractTermsRequest,
} from "@/services/backend/qpm/offers/types/offer";
import {
  ContractStartMode,
  ContractTermMode,
  IoTAdditionalTrapRentMode,
} from "@/services/backend/qpm/offers/types/offer-enum";
import { MonitorType } from "@/services/backend/qpm/types/enums";
import { useOfferContext } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/offer-context";
import {
  fieldErrorsForPrefix,
  resetField,
} from "@/shared/lib/errors/validation-error";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import {
  createSetContractTermsRequestFromOffer,
  defaultContractTermText,
} from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/contract-terms-request";

type OfferContractTermsEditorProps = {
  offer: Offer;
};

type IoTMonitorOption = {
  id: string;
  name: string;
};

function nowAsISOString(): string {
  return new Date().toISOString();
}

function normalizeMonitorSelection(ids: string[]): string[] {
  return Array.from(
    new Set(ids.map((id) => id.trim()).filter((id) => id.length > 0)),
  ).sort((a, b) => a.localeCompare(b));
}

function normalizeRequest(
  request: SetOfferContractTermsRequest,
): SetOfferContractTermsRequest {
  const contractTermText = request.contractTermText.trim();
  return {
    ...request,
    contractTermText,
    contractTermUnlimitedText: contractTermText,
    contractSpecialAgreementText: request.contractSpecialAgreementText.trim(),
    ioTAdditionalTrapRentPrice: Math.max(0, request.ioTAdditionalTrapRentPrice),
    ioTAdditionalTrapRentMonitorIds: normalizeMonitorSelection(
      request.ioTAdditionalTrapRentMonitorIds,
    ),
  };
}

function requestsEqual(
  left: SetOfferContractTermsRequest | null,
  right: SetOfferContractTermsRequest,
): boolean {
  if (!left) {
    return false;
  }
  return JSON.stringify(left) === JSON.stringify(right);
}

function IoTMonitorMultiSelect({
  options,
  selectedIds,
  disabled,
  onChange,
}: {
  options: IoTMonitorOption[];
  selectedIds: string[];
  disabled: boolean;
  onChange: (next: string[]) => void;
}) {
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const label = useMemo(() => {
    if (selectedIds.length === 0) {
      return t("Keine Auswahl");
    }

    const selectedLabels = selectedIds
      .map((id) => options.find((option) => option.id === id)?.name ?? id)
      .filter((value) => value.trim().length > 0);

    if (selectedLabels.length <= 2) {
      return selectedLabels.join(", ");
    }

    return `${selectedLabels.length} ${t("ausgewählt")}`;
  }, [options, selectedIds]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0" align="start">
        <div className="flex items-center justify-end border-b p-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled || selectedIds.length === 0}
            onClick={() => onChange([])}
          >
            {t("Alle abwählen")}
          </Button>
        </div>
        <Command>
          <CommandInput placeholder={t("Suchen")} />
          <CommandList>
            <CommandEmpty>{t("Keine Treffer")}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.id}
                  value={`${option.id} ${option.name}`}
                  onSelect={() => {
                    const next = selectedSet.has(option.id)
                      ? selectedIds.filter((id) => id !== option.id)
                      : [...selectedIds, option.id];
                    onChange(next);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selectedSet.has(option.id) ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {option.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function OfferContractTermsEditor({
  offer,
}: OfferContractTermsEditorProps) {
  const { validationError, setValidationError } = useOfferContext();
  const fieldErrors = fieldErrorsForPrefix(validationError, "");

  const iotMonitorOptions = useMemo<IoTMonitorOption[]>(
    () =>
      Object.entries(offer.monitorSnaps ?? {})
        .filter(([, snap]) => snap.type === MonitorType.IoT)
        .map(([id, snap]) => ({
          id,
          name: snap.name?.trim() || id,
        }))
        .sort((left, right) => left.name.localeCompare(right.name, "de")),
    [offer.monitorSnaps],
  );

  const [request, setRequest] = useState<SetOfferContractTermsRequest>(() =>
    normalizeRequest(createSetContractTermsRequestFromOffer(offer)),
  );
  const [savedRequest, setSavedRequest] =
    useState<SetOfferContractTermsRequest | null>(() =>
      normalizeRequest(createSetContractTermsRequestFromOffer(offer)),
    );

  const initializedOfferId = useRef<string | null>(null);
  const lastSubmittedRequest = useRef<SetOfferContractTermsRequest | null>(
    null,
  );

  const [setContractTerms, { error, isLoading, isSuccess, reset }] =
    useQpmOfferSetContractTermsMutation();

  useEffect(() => {
    if (initializedOfferId.current === offer.id) {
      return;
    }

    initializedOfferId.current = offer.id;
    const initial = normalizeRequest(
      createSetContractTermsRequestFromOffer(offer),
    );
    setRequest(initial);
    setSavedRequest(initial);
  }, [offer]);

  const normalizedDraft = useMemo(() => normalizeRequest(request), [request]);

  const hasPendingChanges = useMemo(
    () => !requestsEqual(savedRequest, normalizedDraft),
    [normalizedDraft, savedRequest],
  );

  const onDebouncedSetContractTerms = useCallback(
    (nextRequest: SetOfferContractTermsRequest) => {
      lastSubmittedRequest.current = nextRequest;
      setContractTerms(nextRequest);
    },
    [setContractTerms],
  );

  useDebouncedMutationWithPersistenceStateContextUpdate(
    normalizedDraft,
    onDebouncedSetContractTerms,
    isLoading,
    error,
    isSuccess,
    reset,
    500,
    {
      skip: offer.immutable || !hasPendingChanges,
      toastError: true,
    },
  );

  useEffect(() => {
    if (!isSuccess || !lastSubmittedRequest.current) {
      return;
    }

    setSavedRequest(lastSubmittedRequest.current);
  }, [isSuccess]);

  const onStartModeChange = useCallback(
    (next: ContractStartMode) => {
      setValidationError(resetField(validationError, "contractStartMode"));
      setValidationError(resetField(validationError, "contractStartDate"));
      setRequest((current) => {
        if (current.contractStartMode === next) {
          return current;
        }
        if (next === ContractStartMode.OnDate) {
          return {
            ...current,
            contractStartMode: next,
            contractStartDate: current.contractStartDate ?? nowAsISOString(),
          };
        }
        return {
          ...current,
          contractStartMode: next,
          contractStartDate: null,
        };
      });
    },
    [setValidationError, validationError],
  );

  const onStartDateChange = useCallback(
    (next: string | null) => {
      setValidationError(resetField(validationError, "contractStartDate"));
      setRequest((current) => {
        if (
          current.contractStartMode !== ContractStartMode.OnDate ||
          next === null
        ) {
          return current;
        }
        if (current.contractStartDate === next) {
          return current;
        }
        return {
          ...current,
          contractStartDate: next,
        };
      });
    },
    [setValidationError, validationError],
  );

  const onTermModeChange = useCallback(
    (next: ContractTermMode) => {
      setValidationError(resetField(validationError, "contractTermMode"));
      setValidationError(resetField(validationError, "contractTermText"));
      setRequest((current) => {
        if (current.contractTermMode === next) {
          return current;
        }

        const previousDefault = defaultContractTermText(
          current.contractTermMode,
        );
        const nextDefault = defaultContractTermText(next);
        const currentText = current.contractTermText.trim();

        const shouldApplyNextDefault =
          currentText === "" ||
          (previousDefault !== "" && currentText === previousDefault);

        const nextText = shouldApplyNextDefault
          ? nextDefault
          : current.contractTermText;

        return {
          ...current,
          contractTermMode: next,
          contractTermText: nextText,
          contractTermUnlimitedText: nextText,
        };
      });
    },
    [setValidationError, validationError],
  );

  return (
    <div className="space-y-5">
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="grid content-start gap-1.5">
          <Label className="min-h-5 text-sm">{t("Vertragsbeginn")}</Label>
          <Select
            value={request.contractStartMode}
            disabled={offer.immutable}
            onValueChange={(next) =>
              onStartModeChange(next as ContractStartMode)
            }
          >
            <SelectTrigger className="h-9 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ContractStartMode.WithInstallation}>
                {t("mit Installation")}
              </SelectItem>
              <SelectItem value={ContractStartMode.OnDate}>
                {t("zum Datum")}
              </SelectItem>
            </SelectContent>
          </Select>
          <div className="text-destructive min-h-5 text-sm">
            {(fieldErrors?.contractStartMode ?? [])[0] ?? ""}
          </div>
        </div>

        <DateInput
          label={t("Vertragsbeginn Datum")}
          date={request.contractStartDate}
          onDateChange={onStartDateChange}
          disabled={
            offer.immutable ||
            request.contractStartMode !== ContractStartMode.OnDate
          }
          fieldErrors={fieldErrors?.contractStartDate}
          resetFieldError={() =>
            setValidationError(resetField(validationError, "contractStartDate"))
          }
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="grid content-start gap-1.5">
          <Label className="min-h-5 text-sm">{t("Vertragslaufzeit")}</Label>
          <Select
            value={request.contractTermMode}
            disabled={offer.immutable}
            onValueChange={(next) => onTermModeChange(next as ContractTermMode)}
          >
            <SelectTrigger className="h-9 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ContractTermMode.Unlimited}>
                {t("Unbefristet")}
              </SelectItem>
              <SelectItem value={ContractTermMode.TwoPlusOne}>2+1</SelectItem>
              <SelectItem value={ContractTermMode.ThreePlusOne}>3+1</SelectItem>
              <SelectItem value={ContractTermMode.ByMainContract}>
                {t("nach Hauptvertrag")}
              </SelectItem>
              <SelectItem value={ContractTermMode.ByFrameworkContract}>
                {t("nach Rahmenvertrag")}
              </SelectItem>
              <SelectItem value={ContractTermMode.Custom}>
                {t("Individuell")}
              </SelectItem>
            </SelectContent>
          </Select>
          <div className="text-destructive min-h-5 text-sm">
            {(fieldErrors?.contractTermMode ?? [])[0] ?? ""}
          </div>
        </div>

        <div className="grid content-start gap-1.5">
          <Label className="min-h-5 text-sm">
            {t("Laufzeittext (Tabelle)")}
          </Label>
          <Textarea
            rows={3}
            value={request.contractTermText}
            disabled={offer.immutable}
            onChange={(event) => {
              const next = event.target.value;
              setValidationError(
                resetField(validationError, "contractTermText"),
              );
              setRequest((current) => {
                if (current.contractTermText === next) {
                  return current;
                }
                return {
                  ...current,
                  contractTermText: next,
                  contractTermUnlimitedText: next,
                };
              });
            }}
          />
          <div className="text-destructive min-h-5 text-sm">
            {(fieldErrors?.contractTermText ?? [])[0] ??
              (fieldErrors?.contractTermUnlimitedText ?? [])[0] ??
              ""}
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="grid content-start gap-2">
          <Label className="min-h-5 text-sm">
            {t("Separater Vertragstext")}
          </Label>
          <div className="flex items-center gap-2 rounded-md border p-2">
            <Checkbox
              checked={request.contractTermTextSeparate}
              disabled={offer.immutable}
              onCheckedChange={(checked) => {
                const next = checked === true;
                setRequest((current) => ({
                  ...current,
                  contractTermTextSeparate: next,
                }));
              }}
            />
            <span className="text-sm">
              {t("Abweichenden Laufzeittext im Vertrag verwenden")}
            </span>
          </div>
        </div>

        <div className="grid content-start gap-1.5">
          <Label className="min-h-5 text-sm">
            {t("Laufzeittext (Vertrag)")}
          </Label>
          <Textarea
            rows={3}
            value={request.contractTermContractText}
            disabled={offer.immutable || !request.contractTermTextSeparate}
            onChange={(event) => {
              const next = event.target.value;
              setValidationError(
                resetField(validationError, "contractTermContractText"),
              );
              setRequest((current) => ({
                ...current,
                contractTermContractText: next,
              }));
            }}
          />
          <div className="text-destructive min-h-5 text-sm">
            {(fieldErrors?.contractTermContractText ?? [])[0] ?? ""}
          </div>
        </div>
      </div>

      <div className="grid content-start gap-1.5">
        <Label className="min-h-5 text-sm">
          {t("Zusätzlicher Vertragstext")}
        </Label>
        <Textarea
          rows={3}
          value={request.contractAdditionalText}
          disabled={offer.immutable}
          onChange={(event) => {
            const next = event.target.value;
            setValidationError(
              resetField(validationError, "contractAdditionalText"),
            );
            setRequest((current) => ({
              ...current,
              contractAdditionalText: next,
            }));
          }}
        />
        <div className="text-destructive min-h-5 text-sm">
          {(fieldErrors?.contractAdditionalText ?? [])[0] ?? ""}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="grid content-start gap-2">
          <Label className="min-h-5 text-sm">
            {t("Punkt 10 Angebot als Grundlage")}
          </Label>
          <div className="flex items-center gap-2 rounded-md border p-2">
            <Checkbox
              checked={request.contractSpecialAgreementUseDate}
              disabled={offer.immutable}
              onCheckedChange={(checked) => {
                const next = checked === true;
                setRequest((current) => ({
                  ...current,
                  contractSpecialAgreementUseDate: next,
                }));
              }}
            />
            <span className="text-sm">
              {t("Vorgefüllten Angebotstext verwenden")}
            </span>
          </div>
        </div>

        <div className="grid content-start gap-1.5">
          <Label className="min-h-5 text-sm">
            {t("Punkt 10 zusätzlicher Text")}
          </Label>
          <Textarea
            rows={3}
            value={request.contractSpecialAgreementText}
            disabled={offer.immutable}
            onChange={(event) => {
              const next = event.target.value;
              setValidationError(
                resetField(validationError, "contractSpecialAgreementText"),
              );
              setRequest((current) => ({
                ...current,
                contractSpecialAgreementText: next,
              }));
            }}
          />
          <div className="text-destructive min-h-5 text-sm">
            {(fieldErrors?.contractSpecialAgreementText ?? [])[0] ?? ""}
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="grid content-start gap-2">
          <Label className="min-h-5 text-sm">
            {t("IoT Service in QPM enthalten")}
          </Label>
          <div className="flex items-center gap-2 rounded-md border p-2">
            <Checkbox
              checked={request.includeIoTCheckpointServiceInQpm}
              disabled={offer.immutable}
              onCheckedChange={(checked) => {
                setRequest((current) => ({
                  ...current,
                  includeIoTCheckpointServiceInQpm: checked === true,
                }));
              }}
            />
            <span className="text-sm">
              {t("Zusätzlichen IoT Checkpoint Service in QPM anzeigen")}
            </span>
          </div>
        </div>

        <div className="grid content-start gap-2">
          <Label className="min-h-5 text-sm">
            {t("Für jede weitere Falle berechnen wir")}
          </Label>
          <div className="flex items-center gap-2 rounded-md border p-2">
            <Checkbox
              checked={request.ioTAdditionalTrapRentEnabled}
              disabled={offer.immutable}
              onCheckedChange={(checked) => {
                const next = checked === true;
                setRequest((current) => ({
                  ...current,
                  ioTAdditionalTrapRentEnabled: next,
                }));
              }}
            />
            <span className="text-sm">{t("Aktiv")}</span>
          </div>
        </div>

        <div className="grid content-start gap-1.5">
          <Label className="min-h-5 text-sm">{t("Preis-Modus")}</Label>
          <Select
            value={request.ioTAdditionalTrapRentMode}
            disabled={offer.immutable || !request.ioTAdditionalTrapRentEnabled}
            onValueChange={(next) => {
              setRequest((current) => ({
                ...current,
                ioTAdditionalTrapRentMode: next as IoTAdditionalTrapRentMode,
              }));
            }}
          >
            <SelectTrigger className="h-9 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={IoTAdditionalTrapRentMode.General}>
                {t("Allgemeiner Preis")}
              </SelectItem>
              <SelectItem value={IoTAdditionalTrapRentMode.PerMonitor}>
                {t("Je Monitor")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="grid content-start gap-1.5">
          <Label className="min-h-5 text-sm">{t("Zusatzfallenpreis")}</Label>
          <div className="flex items-center gap-2">
            <FloatNumberInput
              className="w-full max-w-44"
              value={request.ioTAdditionalTrapRentPrice}
              disabled={
                offer.immutable ||
                !request.ioTAdditionalTrapRentEnabled ||
                request.ioTAdditionalTrapRentMode !==
                  IoTAdditionalTrapRentMode.General
              }
              onChange={(next) => {
                setValidationError(
                  resetField(validationError, "ioTAdditionalTrapRentPrice"),
                );
                setRequest((current) => ({
                  ...current,
                  ioTAdditionalTrapRentPrice: Math.max(0, next),
                }));
              }}
            />
            <span className="text-muted-foreground text-sm">€</span>
          </div>
          <div className="text-destructive min-h-5 text-sm">
            {(fieldErrors?.ioTAdditionalTrapRentPrice ?? [])[0] ?? ""}
          </div>
        </div>

        <div className="grid content-start gap-1.5">
          <Label className="min-h-5 text-sm">
            {t("IoT-Monitore (Je Monitor)")}
          </Label>
          <IoTMonitorMultiSelect
            options={iotMonitorOptions}
            selectedIds={request.ioTAdditionalTrapRentMonitorIds}
            disabled={
              offer.immutable ||
              !request.ioTAdditionalTrapRentEnabled ||
              request.ioTAdditionalTrapRentMode !==
                IoTAdditionalTrapRentMode.PerMonitor
            }
            onChange={(next) => {
              setValidationError(
                resetField(validationError, "ioTAdditionalTrapRentMonitorIds"),
              );
              setRequest((current) => ({
                ...current,
                ioTAdditionalTrapRentMonitorIds:
                  normalizeMonitorSelection(next),
              }));
            }}
          />
          <div className="text-destructive min-h-5 text-sm">
            {(fieldErrors?.ioTAdditionalTrapRentMonitorIds ?? [])[0] ?? ""}
          </div>
        </div>
      </div>
    </div>
  );
}
